import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DialpadRoundedIcon from '@mui/icons-material/DialpadRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { InputSlots, Keypad } from '../components/DigitKeypad';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import useMiniTaskCountdown from '../useMiniTaskCountdown';
import { playMiniTaskSound } from '../soundEffects';
import { withTurkishGenitive } from '../turkishSuffixes';

const SHOW_FEEDBACK_MS = 1250;
const INTRO_MS = 900;

function DigitBackdrop() {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <Box sx={{ position: 'absolute', left: '-5%', top: { xs: '27%', md: '25%' }, width: { xs: 112, md: 180 }, height: { xs: 38, md: 58 }, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.88)', filter: 'drop-shadow(0 5px 0 rgba(255,255,255,0.34))', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: { xs: 50, md: 76 }, height: { xs: 50, md: 76 }, left: 18, top: { xs: -22, md: -34 } }, '&::after': { width: { xs: 62, md: 92 }, height: { xs: 62, md: 92 }, right: 12, top: { xs: -30, md: -44 } } }} />
      <Box sx={{ position: 'absolute', right: '-4%', top: { xs: '34%', md: '31%' }, width: { xs: 116, md: 190 }, height: { xs: 40, md: 60 }, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.86)', filter: 'drop-shadow(0 5px 0 rgba(255,255,255,0.30))', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: { xs: 52, md: 80 }, height: { xs: 52, md: 80 }, left: 16, top: { xs: -23, md: -35 } }, '&::after': { width: { xs: 64, md: 98 }, height: { xs: 64, md: 98 }, right: 10, top: { xs: -31, md: -47 } } }} />
      <Box sx={{ position: 'absolute', left: '-8%', right: '-8%', bottom: -120, height: { xs: 220, md: 270 }, borderRadius: '50% 50% 0 0', bgcolor: '#b7df55', transform: 'rotate(-2deg)', boxShadow: 'inset 0 20px 0 rgba(255,255,255,0.18)' }} />
      <Box sx={{ position: 'absolute', left: '-12%', right: '18%', bottom: -150, height: { xs: 210, md: 255 }, borderRadius: '50% 50% 0 0', bgcolor: '#9fd449', opacity: 0.78, transform: 'rotate(-5deg)' }} />
      <Box sx={{ position: 'absolute', left: '56%', right: '-10%', bottom: -100, height: { xs: 205, md: 260 }, borderRadius: '50% 50% 0 0', bgcolor: '#84c63e', transform: 'rotate(-7deg)' }} />
      <Box sx={{ position: 'absolute', left: { xs: 20, md: 86 }, bottom: { xs: 38, md: 58 }, color: '#facc15', fontSize: { xs: 28, md: 42 }, transform: 'rotate(-14deg)', filter: 'drop-shadow(0 3px 0 rgba(180,83,9,0.20))' }}>★</Box>
      <Box sx={{ position: 'absolute', right: { xs: 28, md: 110 }, bottom: { xs: 52, md: 72 }, color: '#fb7185', fontSize: { xs: 25, md: 38 } }}>✿</Box>
      <Box sx={{ position: 'absolute', left: { xs: 58, md: 180 }, bottom: { xs: 18, md: 30 }, color: '#ffffff', fontSize: { xs: 18, md: 26 }, textShadow: '0 2px 0 rgba(68,111,34,0.22)' }}>✿</Box>
      <Box sx={{ position: 'absolute', right: { xs: 72, md: 230 }, bottom: { xs: 18, md: 34 }, color: '#ffffff', fontSize: { xs: 16, md: 24 }, textShadow: '0 2px 0 rgba(68,111,34,0.22)' }}>✿</Box>
      <Box sx={{ position: 'absolute', left: { xs: 20, md: 120 }, top: '45%', color: '#f5e8ff', fontSize: 28 }}>✦</Box>
      <Box sx={{ position: 'absolute', right: { xs: 25, md: 140 }, top: '49%', color: '#dbeafe', fontSize: 24 }}>✦</Box>
    </Box>
  );
}

function CenterState({ icon, title, detail, children }) {
  return (
    <Box sx={{ minHeight: { xs: 430, md: 500 }, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
      <Box>
        <Box sx={{ width: { xs: 86, md: 112 }, height: { xs: 86, md: 112 }, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#f3e8ff', color: '#6d28d9', boxShadow: '0 8px 0 rgba(109,40,217,0.10)' }}>
          {icon}
        </Box>
        <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 33, md: 50 }, lineHeight: 1.04 }}>
          {title}
        </Typography>
        {detail && (
          <Typography sx={{ mt: 1.2, color: '#6d5d93', fontWeight: 900, fontSize: { xs: 18, md: 26 } }}>
            {detail}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}

function SequenceDots({ currentIndex, total }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
      {Array.from({ length: total }, (_, index) => (
        <Box
          key={index}
          sx={{
            width: index === currentIndex ? { xs: 30, md: 42 } : { xs: 13, md: 16 },
            height: { xs: 13, md: 16 },
            borderRadius: 99,
            bgcolor: index < currentIndex ? '#22c55e' : index === currentIndex ? '#7c3aed' : '#ddd6fe',
            transition: 'width 180ms ease, background-color 180ms ease',
          }}
        />
      ))}
    </Box>
  );
}

function DisplayCountdownBar({ durationMs }) {
  return (
    <Box sx={{ width: '100%', maxWidth: { xs: 250, sm: 340, md: 430 }, mx: 'auto' }}>
      <Box
        aria-label="Sayılar kapanana kadar kalan süre"
        sx={{
          height: { xs: 12, md: 16 },
          borderRadius: 99,
          bgcolor: '#ede9fe',
          border: '2px solid #ddd6fe',
          overflow: 'hidden',
          boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.76)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 99,
            transformOrigin: 'left center',
            background: 'linear-gradient(90deg, #7c3aed 0%, #a78bfa 100%)',
            animation: `digitRecallCountdown ${Math.max(300, Number(durationMs || 0))}ms linear forwards`,
            '@keyframes digitRecallCountdown': {
              from: { transform: 'scaleX(1)' },
              to: { transform: 'scaleX(0)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
}

function DigitCards({ value, hidden, inputMode = false }) {
  const digits = String(value || '').split('');
  const compactCards = digits.length >= 7;
  const tightCards = digits.length >= 6;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        gap: { xs: inputMode ? 0.85 : compactCards ? 0.45 : 0.65, sm: 1.1, md: 1.5 },
        mt: { xs: inputMode ? 1.8 : 1.8, md: 3 },
        mb: { xs: inputMode ? 2.2 : 1.8, md: 3 },
      }}
    >
      {digits.map((digit, index) => (
        <Box
          key={`${index}-${digit}`}
          sx={{
            width: {
              xs: inputMode ? compactCards ? 36 : tightCards ? 42 : 48 : compactCards ? 39 : tightCards ? 46 : 52,
              sm: inputMode ? 62 : 68,
              md: inputMode ? 86 : 92,
            },
            height: {
              xs: inputMode ? compactCards ? 51 : tightCards ? 56 : 62 : compactCards ? 56 : tightCards ? 61 : 66,
              sm: inputMode ? 76 : 82,
              md: inputMode ? 100 : 108,
            },
            boxSizing: 'border-box',
            borderRadius: { xs: 2.4, md: 3.2 },
            display: 'grid',
            placeItems: 'center',
            bgcolor: hidden ? '#f1edff' : '#ffffff',
            border: { xs: `3px solid ${inputMode ? '#b99dfa' : '#a78bfa'}`, md: '4px solid #a78bfa' },
            boxShadow: inputMode
              ? '0 4px 0 rgba(109,40,217,0.12), 0 10px 18px rgba(76,29,149,0.08)'
              : '0 6px 0 rgba(109,40,217,0.18), 0 14px 22px rgba(76,29,149,0.12)',
            color: '#251777',
            fontWeight: 1000,
            fontSize: {
              xs: inputMode ? compactCards ? 25 : tightCards ? 28 : 31 : compactCards ? 28 : tightCards ? 31 : 34,
              sm: inputMode ? 40 : 45,
              md: inputMode ? 60 : 66,
            },
            lineHeight: 1,
          }}
        >
          {hidden ? '•' : digit}
        </Box>
      ))}
    </Box>
  );
}

export default function DigitRecallRoundView({
  round,
  boardSessionId,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onComplete,
}) {
  const [responses, setResponses] = useState([]);
  const responsesRef = useRef(responses);
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [currentInput, setCurrentInput] = useState('');
  const [phase, setPhase] = useState('intro');
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);
  const completionStarted = useRef(false);
  const finishReasonRef = useRef('completed');
  const remainingSeconds = useMiniTaskCountdown({
    roundId: round.round_id,
    boardSessionId,
    durationSeconds: round.duration_seconds,
    active: phase !== 'intro' && phase !== 'completing' && phase !== 'completion_error',
    onExpire: () => completeRound(responsesRef.current, 'timer'),
    persist: false,
  });

  const sequences = useMemo(() => (round.sequences || []).map(String), [round.sequences]);
  const sequenceCount = Number(round.sequence_count || sequences.length || 0);
  const digitLengths = useMemo(
    () => (round.digit_lengths || sequences.map(() => round.digit_length)).map(Number),
    [round.digit_length, round.digit_lengths, sequences],
  );
  const displayTimes = useMemo(
    () => (round.display_ms_by_sequence || sequences.map(() => round.display_ms)).map(Number),
    [round.display_ms, round.display_ms_by_sequence, sequences],
  );
  const digitLength = Number(digitLengths[sequenceIndex] || round.digit_length || 0);
  const currentSequence = sequences[sequenceIndex] || '';
  const studentName = round.student?.display_name || 'Öğrenci';

  const completeRound = async (nextResponses, finishReason = 'completed') => {
    if (completionStarted.current) return;
    completionStarted.current = true;
    finishReasonRef.current = finishReason;
    setPhase('completing');
    setError('');
    try {
      await onComplete(nextResponses, finishReason);
    } catch (completionError) {
      completionStarted.current = false;
      setError(completionError?.message || 'Sonucun kaydedilemedi.');
      setPhase('completion_error');
    }
  };

  useEffect(() => {
    if (phase !== 'intro') return undefined;
    const timer = window.setTimeout(() => setPhase('showing'), INTRO_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'showing') return undefined;
    const timer = window.setTimeout(
      () => setPhase('input'),
      Number(displayTimes[sequenceIndex] || round.display_ms || 5000),
    );
    return () => window.clearTimeout(timer);
  }, [displayTimes, phase, round.display_ms, sequenceIndex]);

  const handleDigit = (digit) => {
    if (phase !== 'input' || currentInput.length >= digitLength) return;
    setCurrentInput((current) => `${current}${digit}`);
  };

  const handleDelete = () => {
    if (phase !== 'input') return;
    setCurrentInput((current) => current.slice(0, -1));
  };

  const advanceAfterFeedback = (nextResponses) => {
    window.setTimeout(() => {
      if (completionStarted.current || finishReasonRef.current === 'timer') return;
      setFeedback(null);
      setCurrentInput('');
      if (nextResponses.length >= sequenceCount) {
        completeRound(nextResponses);
        return;
      }
      setSequenceIndex(nextResponses.length);
      setPhase('showing');
    }, SHOW_FEEDBACK_MS);
  };

  const handleSubmit = () => {
    if (phase !== 'input' || currentInput.length !== digitLength) return;
    const isCorrect = currentInput === currentSequence;
    const nextResponses = [...responses, currentInput];
    setResponses(nextResponses);
    responsesRef.current = nextResponses;
    setFeedback({
      type: isCorrect ? 'correct' : 'near',
      message: isCorrect ? 'Harika, doğru sıra!' : `Neredeyse! Doğru sıra ${currentSequence}.`,
    });
    setPhase('feedback');
    playMiniTaskSound(isCorrect ? 'correct' : 'wrong', soundEnabled);
    advanceAfterFeedback(nextResponses);
  };

  const handleConfirmedExit = () => {
    setExitOpen(false);
    onExit();
  };

  const retryCompletion = () => completeRound(responsesRef.current, finishReasonRef.current);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        minHeight: '100vh',
        bgcolor: '#fffaf0',
        color: '#172554',
        overflow: 'hidden',
        backgroundImage: 'radial-gradient(circle at 50% 26%, rgba(255,255,255,0.88) 0 9%, rgba(255,255,255,0) 24%), linear-gradient(180deg, #fffaf0 0%, #fff7df 67%, #f7f4d6 100%)',
      }}
    >
      {round.preview && (
        <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>
          TEST MODU
        </Box>
      )}
      <DigitBackdrop />

      <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', maxWidth: 1720, px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 2, md: 3 } }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', md: '220px minmax(0, 1fr) 220px' }, alignItems: 'center', gap: { xs: 1, md: 2 }, mt: { xs: 1.7, md: 3.2 }, mb: { xs: 2.3, md: 2.8 } }}>
          <ButtonBase
            onClick={() => setExitOpen(true)}
            disabled={phase === 'completing'}
            sx={{ justifySelf: 'start', gridColumn: { xs: 1, md: 1 }, gridRow: 1, borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.88)', color: '#33258a', px: { xs: 1.4, md: 2.2 }, py: { xs: 0.85, md: 1.2 }, minWidth: { xs: 0, sm: 142, md: 210 }, minHeight: { xs: 52, md: 60 }, boxShadow: '0 4px 0 rgba(91,33,182,0.10)', display: 'flex', alignItems: 'center', gap: 0.8 }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 24, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>Ana sayfa</Typography>
          </ButtonBase>

          <Typography sx={{ gridColumn: { xs: '1 / -1', md: 2 }, gridRow: { xs: 2, md: 1 }, textAlign: 'center', color: '#251777', fontWeight: 1000, fontSize: { xs: 26, sm: 40, md: 48 }, lineHeight: 1.04, overflowWrap: 'anywhere', textShadow: '0 3px 0 rgba(255,255,255,0.94)' }}>
            {withTurkishGenitive(studentName)} Zihin Görevi
          </Typography>

          <Box sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, justifySelf: 'end', borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.88)', color: '#33258a', px: { xs: 1.1, md: 2.2 }, py: { xs: 0.85, md: 1.2 }, minWidth: { xs: 0, sm: 126, md: 210 }, textAlign: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>Sıra {Math.min(sequenceIndex + 1, sequenceCount)} / {sequenceCount}</Typography>
            <Typography role="timer" sx={{ fontWeight: 900, fontSize: { xs: 13, md: 17 } }}>Süre: {remainingSeconds} sn</Typography>
          </Box>
        </Box>

        <Box sx={{ width: { xs: 'calc(100vw - 20px)', sm: '100%' }, boxSizing: 'border-box', maxWidth: 1260, mx: 'auto', mt: { xs: 1.1, md: 0.4 }, position: 'relative', borderRadius: { xs: 3, md: 4 }, bgcolor: 'rgba(255,255,255,0.96)', border: '2px solid #e9d5ff', boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)', px: { xs: 1, sm: 2.2, md: 4 }, pt: { xs: 4.8, md: 5.2 }, pb: { xs: 1.5, md: 2 }, minHeight: { xs: 560, md: 640 } }}>
          <Box sx={{ position: 'absolute', left: '50%', top: { xs: 2, md: 0 }, transform: 'translate(-50%, -50%)', borderRadius: 2.5, border: '2px solid #d8b4fe', bgcolor: '#f5f0ff', color: '#6d45df', px: { xs: 1.2, md: 2.2 }, py: { xs: 0.55, md: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.8, width: { xs: 'min(250px, calc(100% - 34px))', md: 310 }, justifyContent: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <DialpadRoundedIcon sx={{ color: '#7c3aed', fontSize: { xs: 27, md: 34 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 17, md: 23 }, lineHeight: 1 }}>Numara Hatırlama</Typography>
          </Box>

          {phase === 'intro' && (
            <CenterState
              icon={<DialpadRoundedIcon sx={{ fontSize: { xs: 56, md: 74 } }} />}
              title="Numara Hatırlama"
              detail="Rakamları aklında tut."
            >
              <Box sx={{ mt: 2.5 }}><SequenceDots currentIndex={sequenceIndex} total={sequenceCount} /></Box>
            </CenterState>
          )}

          {phase === 'showing' && (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, color: '#4c1d95', fontWeight: 1000, fontSize: { xs: 19, md: 25 } }}>
                <VisibilityRoundedIcon sx={{ color: '#7c3aed' }} />
                <Typography sx={{ fontWeight: 1000, fontSize: { xs: 19, md: 25 } }}>Rakamları aklında tut</Typography>
              </Box>
              <DigitCards value={currentSequence} />
              <Box sx={{ minHeight: { xs: 58, md: 78 }, display: 'grid', placeItems: 'center', color: '#37306f' }}>
                <DisplayCountdownBar durationMs={round.display_ms} />
              </Box>
              <SequenceDots currentIndex={sequenceIndex} total={sequenceCount} />
            </Box>
          )}

          {phase === 'input' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#4c1d95', fontWeight: 1000, fontSize: { xs: 19, md: 25 } }}>Gördüğün sırayı gir</Typography>
              <DigitCards value={currentSequence} hidden inputMode />
              <InputSlots value={currentInput} length={digitLength} />
              <Keypad
                disabled={false}
                onDigit={handleDigit}
                onDelete={handleDelete}
                onSubmit={handleSubmit}
                submitDisabled={currentInput.length !== digitLength}
              />
              <Box sx={{ minHeight: { xs: 46, md: 58 }, mt: 1.2, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#37306f', flexWrap: 'wrap' }}>
                <Box sx={{ width: { xs: 32, md: 38 }, height: { xs: 32, md: 38 }, borderRadius: '50%', bgcolor: '#a78bfa', color: '#fff', display: 'grid', placeItems: 'center', boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.12)' }}>
                  <FavoriteRoundedIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                </Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 13, md: 19 }, lineHeight: 1.2, textAlign: 'center', maxWidth: { xs: 250, sm: 'none' } }}>Büyük tuşlara dokun ve sırayı tamamla.</Typography>
              </Box>
              <SequenceDots currentIndex={sequenceIndex} total={sequenceCount} />
            </Box>
          )}

          {phase === 'feedback' && (
            <CenterState
              icon={<CheckCircleRoundedIcon sx={{ fontSize: { xs: 58, md: 78 }, color: feedback?.type === 'correct' ? '#22c55e' : '#f59e0b' }} />}
              title={feedback?.type === 'correct' ? 'Doğru sıra!' : 'Neredeyse!'}
              detail={feedback?.message}
            />
          )}

          {(phase === 'completing' || phase === 'completion_error') && (
            <CenterState
              icon={phase === 'completing'
                ? <CircularProgress size={52} />
                : <DialpadRoundedIcon sx={{ fontSize: { xs: 58, md: 76 }, color: '#f59e0b' }} />}
              title={phase === 'completing' ? 'Harika çalıştın!' : 'Sonucun kaydedilemedi'}
              detail={phase === 'completing' ? 'Sonucun kaydediliyor...' : error}
            >
              {phase === 'completion_error' && (
                <Button variant="contained" onClick={retryCompletion} sx={{ mt: 2.5, borderRadius: 99, px: 4, py: 1.2, bgcolor: '#6d45df', fontWeight: 1000, fontSize: 18 }}>
                  Tekrar dene
                </Button>
              )}
            </CenterState>
          )}
        </Box>
      </Box>

      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, border: '2px solid #e9d5ff' } }}>
        <DialogTitle sx={{ color: '#251777', fontWeight: 1000, fontSize: 26 }}>Görevden çıkmak istiyor musun?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6d5d93', fontWeight: 800 }}>Bu turdaki ilerlemen kaydedilmeyecek.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExitOpen(false)} autoFocus sx={{ fontWeight: 900 }}>Göreve dön</Button>
          <Button color="error" variant="contained" onClick={handleConfirmedExit} sx={{ borderRadius: 99, fontWeight: 900 }}>Çık</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
